/**
 * TabBar Component (enhanced in Task 10.4)
 *
 * Displays scene tabs with support for:
 * - Up to 10 tabs horizontally
 * - Active tab highlighting with underline
 * - New tab creation button
 * - Tab overflow scrolling
 * - Accessible keyboard navigation using Radix Tabs
 * - Smooth animations with Framer Motion (Task 10.4)
 */

import { useEffect, useRef } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import { motion } from 'framer-motion';
import { Plus } from 'lucide-react';
import { useSceneStore } from '../../stores/sceneStore';
import { TabHeader } from './TabHeader';

export function TabBar() {
  const tabs = useSceneStore((state) => state.tabs);
  const activeTabId = useSceneStore((state) => state.activeTabId);
  const createTab = useSceneStore((state) => state.createTab);
  const setActiveTab = useSceneStore((state) => state.setActiveTab);

  const tabsContainerRef = useRef<HTMLDivElement>(null);

  // Scroll to active tab when it changes
  useEffect(() => {
    if (activeTabId && tabsContainerRef.current) {
      const activeTabElement = tabsContainerRef.current.querySelector(
        `[data-tab-id="${activeTabId}"]`
      );
      if (
        activeTabElement &&
        typeof activeTabElement.scrollIntoView === 'function'
      ) {
        activeTabElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }
  }, [activeTabId]);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  const handleNewTab = () => {
    createTab();
  };

  // Create a sorted copy of tabs to avoid mutating the frozen array from Zustand
  const sortedTabs = [...tabs].sort((a, b) => a.order - b.order);

  return (
    <div className="w-full border-b border-neutral-700 bg-neutral-900">
      <Tabs.Root
        value={activeTabId || undefined}
        onValueChange={handleTabChange}
        className="w-full"
      >
        <div className="flex items-center">
          {/* Tabs Container with horizontal scroll */}
          <Tabs.List
            ref={tabsContainerRef}
            className="flex flex-1 items-center gap-1 overflow-x-auto overflow-y-hidden px-2 py-1 scrollbar-thin scrollbar-track-neutral-800 scrollbar-thumb-neutral-600"
            style={{ maxHeight: '40px' }}
          >
            {sortedTabs.map((tab) => (
              <TabHeader key={tab.id} tab={tab} />
            ))}
          </Tabs.List>

          {/* New Tab Button with Framer Motion (Task 10.4) */}
          <motion.button
            onClick={handleNewTab}
            disabled={tabs.length >= 10}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-neutral-700 bg-neutral-800 text-neutral-400 transition-colors hover:bg-neutral-700 hover:text-neutral-200 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-neutral-800 disabled:hover:text-neutral-400 mr-2"
            aria-label="Create new tab"
            title={
              tabs.length >= 10 ? 'Maximum 10 tabs allowed' : 'Create new tab'
            }
            whileHover={tabs.length < 10 ? { scale: 1.05 } : {}}
            whileTap={tabs.length < 10 ? { scale: 0.95 } : {}}
          >
            <Plus className="h-4 w-4" />
          </motion.button>
        </div>
      </Tabs.Root>
    </div>
  );
}
